import { Schema } from 'rsuite';
import { AllegroReturnPolicyRestrictionCauseName } from '../../../../../api/allegro/returnpolicy/returnPolicyTypes';
import { Edit } from './returnPolicyState';
import { getRestrictionCauses, requiresPolicyDetails, requiresRestrictionCause } from './returnPolicyRules';

const { StringType } = Schema.Types;

const POST_CODE_PATTERNS: Record<string, RegExp> = {
  PL: /^\d{2}-\d{3}$/,
  CZ: /^\d{3} \d{2}$/,
  SK: /^\d{3} \d{2}$/,
};
const DEFAULT_POST_CODE_PATTERN = /^[A-Za-z0-9 -]{1,16}$/;

const DEFAULT_MAX_LENGTH = 200;

const NAME_MAX_LENGTHS_BY_COUNTRY: Record<string, number> = {
  CZ: 50,
  SK: 50,
};

const STREET_MAX_LENGTHS_BY_COUNTRY: Record<string, number> = {
  PL: 35,
  CZ: 35,
  SK: 35,
};

const CITY_MAX_LENGTHS_BY_COUNTRY: Record<string, number> = {
  PL: 30,
  CZ: 30,
  SK: 30,
};

function validatePolicyDetailRequired(value: string, edit: Edit): boolean {
  return !requiresPolicyDetails(edit.range) || !!value;
}

function validateRestrictionCauseRequired(value: string, edit: Edit): boolean {
  return !requiresRestrictionCause(edit.range) || !!value;
}

function validateRestrictionCauseInRange(value: string, edit: Edit): boolean {
  if (!value || !requiresRestrictionCause(edit.range)) {
    return true;
  }

  return getRestrictionCauses(edit.range)
    .includes(value as AllegroReturnPolicyRestrictionCauseName);
}

function validatePostCodeFormat(value: string, edit: Edit): boolean {
  if (!requiresPolicyDetails(edit.range) || !value) {
    return true;
  }

  const pattern = POST_CODE_PATTERNS[edit.countryCode ?? ''] ?? DEFAULT_POST_CODE_PATTERN;

  return pattern.test(value);
}

function validateNameLength(value: string, edit: Edit) {
  if (!requiresPolicyDetails(edit.range) || !value) {
    return true;
  }

  const max = NAME_MAX_LENGTHS_BY_COUNTRY[edit.countryCode ?? ''] ?? DEFAULT_MAX_LENGTH;

  if (value.length <= max) {
    return true;
  }

  return { hasError: true, errorMessage: `Max ${max} characters.` };
}

function validateStreetLength(value: string, edit: Edit) {
  if (!requiresPolicyDetails(edit.range) || !value) {
    return true;
  }

  const max = STREET_MAX_LENGTHS_BY_COUNTRY[edit.countryCode ?? ''] ?? DEFAULT_MAX_LENGTH;

  if (value.length <= max) {
    return true;
  }

  return { hasError: true, errorMessage: `Max ${max} characters.` };
}

function validateCityLength(value: string, edit: Edit) {
  if (!requiresPolicyDetails(edit.range) || !value) {
    return true;
  }

  const max = CITY_MAX_LENGTHS_BY_COUNTRY[edit.countryCode ?? ''] ?? DEFAULT_MAX_LENGTH;

  if (value.length <= max) {
    return true;
  }

  return { hasError: true, errorMessage: `Max ${max} characters.` };
}

const validationModel = Schema.Model({
  label: StringType()
    .isRequired('Label is required.')
    .maxLength(200, 'Max 200 characters.'),

  range: StringType()
    .isRequired('Availability range is required.'),

  restrictionCauseName: StringType()
    .addRule(
      validateRestrictionCauseRequired,
      'Restriction cause is required when availability is restricted or disabled.',
      true,
    )
    .addRule(
      validateRestrictionCauseInRange,
      'Selected restriction cause is not valid for the chosen availability range.',
    ),

  withdrawalPeriod: StringType()
    .addRule(validatePolicyDetailRequired, 'Withdrawal period is required.', true),

  coveredBy: StringType()
    .addRule(validatePolicyDetailRequired, 'Return cost covered by is required.', true),

  name: StringType()
    .addRule(validatePolicyDetailRequired, 'Name is required.', true)
    .addRule(validateNameLength),

  street: StringType()
    .addRule(validatePolicyDetailRequired, 'Street is required.', true)
    .addRule(validateStreetLength),

  postCode: StringType()
    .addRule(validatePolicyDetailRequired, 'Postal code is required.', true)
    .addRule(validatePostCodeFormat, 'Postal code format does not match the selected country.'),

  city: StringType()
    .addRule(validatePolicyDetailRequired, 'City is required.', true)
    .addRule(validateCityLength),

  countryCode: StringType()
    .addRule(validatePolicyDetailRequired, 'Country code is required.', true),

  email: StringType()
    .maxLength(50, 'Max 50 characters.')
    .isEmail('Email is not valid.'),

  phoneNumber: StringType()
    .maxLength(30, 'Max 30 characters.'),
});

export default validationModel;
